import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { Doughnut } from "react-chartjs-2";
import { ReadOnlyProps } from "../../../utils/templates";

export interface PieSlice {
  label: string;
  value: number;
  percentage: number;
}

interface Props {
  title: string;
  slices: PieSlice[];
  centerText: string;
  centerSubText?: string;
}

const PIE_PALETTE = [
  "#5470C6", // blue
  "#91CC75", // green
  "#FAC858", // yellow
  "#EE6666", // red
  "#73C0DE", // sky blue
  "#3BA272", // forest green
  "#FC8452", // orange
  "#9A60B4", // purple
  "#EA7CCC", // pink
  "#48B8D0", // cyan
  "#EA7455", // coral
  "#516B91", // slate
  "#59C4E6", // light sky
  "#D7AB82", // tan
];

export const PieChartControl: React.FC<Props> = ({
  title,
  slices,
  centerText,
  centerSubText,
}: ReadOnlyProps<Props>) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  if (!slices.length) return null;

  const chartData = {
    labels: slices.map((s) => s.label),
    datasets: [
      {
        data: slices.map((s) => s.value),
        backgroundColor: slices.map((_, i) => {
          const color = PIE_PALETTE[i % PIE_PALETTE.length];
          return hoveredIndex === null || hoveredIndex === i ? color : color + "40"; // dim unhovered
        }),
        hoverBackgroundColor: slices.map((_, i) => PIE_PALETTE[i % PIE_PALETTE.length]),
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    cutoutPercentage: 60,
    tooltips: {
      callbacks: {
        label: (item: any, _data: any) => {
          const s = slices[item.index];
          return ` ${s.label}: ${s.percentage.toFixed(1)}%`;
        },
      },
    },
  };

  return (
    <View>
      {!!title && <Text style={styles.title}>{title}</Text>}

      {/* Donut chart — centered */}
      <View style={styles.chartArea}>
        <View style={styles.chartWrap}>
          <Doughnut data={chartData} options={options} />
          <View
            style={[StyleSheet.absoluteFillObject, styles.centerOverlay]}
            pointerEvents="none"
          >
            <Text style={styles.centerNumber}>{centerText}</Text>
            {centerSubText ? (
              <Text style={styles.centerSub}>{centerSubText}</Text>
            ) : null}
          </View>
        </View>
      </View>

      {/* Legend — 2-column grid */}
      <View style={styles.legend}>
        {slices.map((slice, i) => (
          <LegendItem
            key={slice.label}
            slice={slice}
            color={PIE_PALETTE[i % PIE_PALETTE.length]}
            onHoverChange={(isHovered) => setHoveredIndex(isHovered ? i : null)}
          />
        ))}
      </View>
    </View>
  );
};

const LegendItem = ({ slice, color, onHoverChange }: { slice: PieSlice, color: string, onHoverChange: (hovered: boolean) => void }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <View
      style={styles.legendRow}
      // @ts-ignore: web-only events
      onMouseEnter={() => { setIsHovered(true); onHoverChange(true); }}
      onMouseLeave={() => { setIsHovered(false); onHoverChange(false); }}
    >
      <View
        style={[
          styles.swatch,
          { backgroundColor: color },
          isHovered && styles.swatchHover,
        ]}
      />
      <Text style={[styles.legendName, isHovered && { fontWeight: "600", color: "#111" }]} numberOfLines={1}>
        {slice.label}
      </Text>
      <Text style={styles.legendPct}>{slice.percentage.toFixed(1)}%</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  title:         { fontSize: 14, fontWeight: "600", color: "#555", marginBottom: 12 },
  chartArea:     { alignItems: "center", marginBottom: 14 },
  chartWrap:     { width: 180, height: 180 },
  centerOverlay: { justifyContent: "center", alignItems: "center" },
  centerNumber:  { fontSize: 24, fontWeight: "700", color: "#333" },
  centerSub:     { fontSize: 11, color: "#888", marginTop: 2 },
  legend:        { flexDirection: "row", flexWrap: "wrap" },
  legendRow:     { flexDirection: "row", alignItems: "center", width: "50%", marginBottom: 8, paddingRight: 10, cursor: "pointer" },
  swatch:        { width: 14, height: 14, borderRadius: 7, marginRight: 8, flexShrink: 0, transition: "transform 0.2s ease" },
  swatchHover:   { transform: [{ scale: 1.5 }] },
  legendName:    { flex: 1, fontSize: 13, color: "#444" },
  legendPct:     { fontSize: 13, fontWeight: "600", color: "#666", marginLeft: 4 },
});
